import { CommonModule } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from "@angular/core";
import { NovaIcon } from "@rds-nova/angular-icon";
import { NovaSkeleton } from "@rds-nova/angular-skeleton";
import {
  NovaTable,
  NovaTableBody,
  NovaTableCell,
  NovaTableColumn,
  NovaTableHead,
  NovaTableHeadRow,
  NovaTableRow,
} from "@rds-nova/angular-table";
import { NovaTag } from "@rds-nova/angular-tag";
import { ClosingDetailEntry, ClosingDetailMap } from "../../+state/ron-monitor.store";
import { ClosingPill, NotaryLane, SlotVariant } from "../../models/ron-monitor.models";
import {
  GridLane,
  SLOT_MINUTES,
  SlotAxis,
  SlotColumn,
  SlotPresentation,
  VISIBLE_SLOTS,
  bucketClosings,
  bucketKey,
  buildGridLanes,
  buildSlotAxis,
  clampWindowStart,
  currentSlotIndex,
  designationLabel,
  emptyPresentation,
  fittedSlotCount,
  formatClockTime,
  initialWindowStart,
  isOpenSlot,
  laneIdForClosing,
  matchingLaneIds,
  openSlotPresentation,
  slotPresentation,
} from "../../ron-closing-monitor.utils";
import { OrderDetailPanelComponent } from "../order-detail-panel/order-detail-panel.component";
import { SlotPillComponent } from "./slot-pill.component";

export interface GridDensity {
  key: "comfortable" | "compact" | "dense";
  minHostWidth: number;
  laneWidth: number;
  minSlotWidth: number;
}

/** Widest tier first; the grid takes the first whose minHostWidth its measured width clears. */
export const GRID_DENSITIES: readonly GridDensity[] = [
  { key: "comfortable", minHostWidth: 1520, laneWidth: 178, minSlotWidth: 152 },
  { key: "compact", minHostWidth: 1140, laneWidth: 150, minSlotWidth: 140 },
  { key: "dense", minHostWidth: 0, laneWidth: 130, minSlotWidth: 132 },
];

const ARROW_COLUMN_WIDTH = 24;

const GRID_BOTTOM_GUTTER = 32;
const GRID_MIN_HEIGHT = 240;

export interface GridCell {
  key: string;
  isCurrent: boolean;
  packageId: number | null;
  pill: ClosingPill | null;
  presentation: SlotPresentation;
  interactive: boolean;
  expanded: boolean;
  controls: string | null;
}

export interface GridPanel {
  packageId: number;
  panelId: string;
  variant: SlotVariant;
  ariaLabel: string;
}

export interface GridRow {
  lane: GridLane;
  isMatch: boolean;
  cells: GridCell[];
  panels: GridPanel[];
}

interface SkeletonRow {
  laneWidth: string;
}

const SKELETON_ROWS: readonly SkeletonRow[] = [
  { laneWidth: "74%" },
  { laneWidth: "58%" },
  { laneWidth: "82%" },
  { laneWidth: "66%" },
  { laneWidth: "78%" },
  { laneWidth: "54%" },
];

@Component({
  selector: "app-notary-grid",
  templateUrl: "./notary-grid.component.html",
  styleUrls: ["./notary-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NovaIcon,
    NovaSkeleton,
    NovaTable,
    NovaTableBody,
    NovaTableCell,
    NovaTableColumn,
    NovaTableHead,
    NovaTableHeadRow,
    NovaTableRow,
    NovaTag,
    OrderDetailPanelComponent,
    SlotPillComponent,
  ],
})
export class NotaryGridComponent
  implements OnChanges, AfterViewInit, AfterViewChecked, OnDestroy
{
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);

  @Input() loading = false;
  @Input() loadFailed = false;
  @Input() lanes: NotaryLane[] = [];
  @Input() closings: ClosingPill[] = [];
  @Input() now: Date | null = null;
  @Input() searchTerm = "";
  @Input() expandedPackageIds: number[] = [];
  @Input() details: ClosingDetailMap = {};
  @Input() panelActionsEnabled = false;

  @Output() pillActivate = new EventEmitter<ClosingPill>();
  @Output() panelRetry = new EventEmitter<number>();
  @Output() panelDismiss = new EventEmitter<number>();

  visibleColumns: SlotColumn[] = [];
  skeletonRows: readonly SkeletonRow[] = [];
  rows: GridRow[] = [];
  emptyMessage: string | null = null;
  hasNoResults = false;
  currentSlotColumnIndex: number | null = null;
  canPageBack = false;
  canPageForward = false;
  density: GridDensity = GRID_DENSITIES[0];
  visibleSlots = VISIBLE_SLOTS;
  laneWidth = GRID_DENSITIES[0].laneWidth;
  slotWidth = GRID_DENSITIES[0].minSlotWidth;

  private axis: SlotAxis = buildSlotAxis();
  private windowStart = 0;
  private windowInitialized = false;
  private pendingScrollLaneId: string | null = null;
  private pendingFocusPackageId: number | null = null;
  private pendingDismissPackageIds: number[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private pendingResizeFrame: number | null = null;
  private appliedGridHeight = 0;

  get laneColumnWidth(): string {
    return `${this.laneWidth}px`;
  }

  get arrowColumnWidth(): string {
    return `${ARROW_COLUMN_WIDTH}px`;
  }

  get slotColumnWidth(): string {
    return `${this.slotWidth}px`;
  }

  get panelColumnSpan(): number {
    return this.visibleColumns.length + 3;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.measureAvailableWidth();
    this.rebuild();

    if (changes["searchTerm"] && !changes["searchTerm"].firstChange) {
      this.pendingScrollLaneId =
        this.rows.find((row) => row.isMatch)?.lane.laneId ?? null;
    }
  }

  ngAfterViewInit(): void {
    this.syncGridHeight();

    if (typeof ResizeObserver === "undefined") {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.scheduleResizeSync());
    this.resizeObserver.observe(this.host.nativeElement);
  }

  ngAfterViewChecked(): void {
    this.flushPendingScroll();
    this.flushPendingFocus();
    this.flushPendingDismiss();
    this.syncGridHeight();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.pendingResizeFrame !== null) {
      cancelAnimationFrame(this.pendingResizeFrame);
    }
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.syncToAvailableSpace();
  }

  entryFor(packageId: number): ClosingDetailEntry | null {
    return this.details[packageId] ?? null;
  }

  designationLabel(lane: GridLane): string | null {
    return designationLabel(lane.designation);
  }

  onPanelDismiss(packageId: number): void {
    this.pendingFocusPackageId = packageId;
    this.panelDismiss.emit(packageId);
  }

  pageBy(delta: number): void {
    const next = clampWindowStart(
      this.windowStart + delta,
      this.axis.length,
      this.visibleSlots
    );
    if (next === this.windowStart) {
      return;
    }
    this.windowStart = next;
    this.rebuild();
  }

  onPillActivate(cell: GridCell): void {
    if (cell.pill) {
      this.pillActivate.emit(cell.pill);
    }
  }

  trackByLaneId(_index: number, row: GridRow): string {
    return row.lane.laneId;
  }

  trackByCellKey(_index: number, cell: GridCell): string {
    return cell.key;
  }

  trackBySlotIndex(_index: number, column: SlotColumn): number {
    return column.index;
  }

  trackByPanelId(_index: number, panel: GridPanel): string {
    return panel.panelId;
  }

  trackByIndex(index: number): number {
    return index;
  }

  /** Resizing the grid inside the observer callback re-triggers it in the same frame, which Chrome reports as a "ResizeObserver loop" error. */
  private scheduleResizeSync(): void {
    if (this.pendingResizeFrame !== null) {
      return;
    }
    this.pendingResizeFrame = requestAnimationFrame(() => {
      this.pendingResizeFrame = null;
      this.zone.run(() => this.syncToAvailableSpace());
    });
  }

  private syncToAvailableSpace(): void {
    this.syncGridHeight();

    if (!this.measureAvailableWidth()) {
      return;
    }
    this.rebuild();
    this.changeDetector.markForCheck();
  }

  /** Whole pixels only: fractional widths round up per column and push the last one past the table. */
  private measureAvailableWidth(): boolean {
    const width = this.tableWidth();
    if (width === 0) {
      return false;
    }

    const density =
      GRID_DENSITIES.find((candidate) => width >= candidate.minHostWidth) ??
      GRID_DENSITIES[GRID_DENSITIES.length - 1];
    const slotSpace = width - density.laneWidth - ARROW_COLUMN_WIDTH * 2;
    const slots = fittedSlotCount(slotSpace, density.minSlotWidth);
    const slotWidth = Math.max(
      density.minSlotWidth,
      Math.floor(slotSpace / slots)
    );
    const laneWidth =
      density.laneWidth + Math.max(0, slotSpace - slotWidth * slots);

    if (
      density === this.density &&
      slots === this.visibleSlots &&
      slotWidth === this.slotWidth &&
      laneWidth === this.laneWidth
    ) {
      return false;
    }

    this.density = density;
    this.visibleSlots = slots;
    this.slotWidth = slotWidth;
    this.laneWidth = laneWidth;
    this.windowStart = clampWindowStart(
      this.windowStart,
      this.axis.length,
      slots
    );
    return true;
  }

  /** The wrapper is narrower than the host by its border and any vertical scrollbar. */
  private tableWidth(): number {
    const element = this.host.nativeElement;
    return (
      element.querySelector<HTMLElement>(".nova-table-wrapper")?.clientWidth ??
      element.clientWidth
    );
  }

  /** Bounds the scrolling body so the page itself does not scroll behind it. */
  private syncGridHeight(): void {
    const element = this.host.nativeElement;
    const view = element.ownerDocument.defaultView;
    const documentTop =
      element.getBoundingClientRect().top + (view?.scrollY ?? 0);
    const available = Math.max(
      GRID_MIN_HEIGHT,
      Math.round((view?.innerHeight ?? 0) - documentTop - GRID_BOTTOM_GUTTER)
    );

    if (Math.abs(available - this.appliedGridHeight) < 8) {
      return;
    }
    this.appliedGridHeight = available;
    element.style.setProperty("--rcm-grid-max-height", `${available}px`);
  }

  private flushPendingScroll(): void {
    if (this.pendingScrollLaneId === null) {
      return;
    }
    const laneId = this.pendingScrollLaneId;
    this.pendingScrollLaneId = null;
    this.host.nativeElement
      .querySelector(`[data-lane-id="${CSS.escape(laneId)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }

  private flushPendingFocus(): void {
    if (this.pendingFocusPackageId === null) {
      return;
    }
    const packageId = this.pendingFocusPackageId;
    if (this.expandedPackageIds.includes(packageId)) {
      return;
    }
    this.pendingFocusPackageId = null;
    this.host.nativeElement
      .querySelector<HTMLElement>(
        `[data-package-id="${packageId}"] .rcm-pill--interactive`
      )
      ?.focus();
  }

  private rebuild(): void {
    if (!this.loading || !this.windowInitialized) {
      this.adoptAxis(buildSlotAxis(this.closings));
    }
    const lanes = buildGridLanes(this.lanes, this.closings);
    const buckets = bucketClosings(this.closings, this.axis);
    const current = this.now ? currentSlotIndex(this.now, this.axis) : null;

    if (!this.windowInitialized && lanes.length > 0) {
      this.windowStart = initialWindowStart(
        this.now,
        this.axis,
        this.visibleSlots
      );
      this.windowInitialized = true;
    }
    this.windowStart = clampWindowStart(
      this.windowStart,
      this.axis.length,
      this.visibleSlots
    );

    const isSearching = this.searchTerm.trim() !== "";
    const matches = matchingLaneIds(this.searchTerm, lanes, this.closings);
    const expanded = new Set(this.expandedPackageIds);
    this.currentSlotColumnIndex = current;

    this.visibleColumns = this.axis.slice(
      this.windowStart,
      this.windowStart + this.visibleSlots
    );

    const cellsByLane = lanes.map((lane) =>
      this.visibleColumns.map((column) =>
        this.buildCell(lane, column, buckets, current, expanded)
      )
    );
    const inView = new Set<number>();
    cellsByLane.forEach((cells) =>
      cells.forEach((cell) => {
        if (cell.packageId !== null) {
          inView.add(cell.packageId);
        }
      })
    );

    const allRows = lanes.map((lane, index) => ({
      lane,
      isMatch: matches.has(lane.laneId),
      cells: cellsByLane[index],
      panels: this.buildPanels(lane, expanded, inView),
    }));
    this.rows = isSearching ? allRows.filter((row) => row.isMatch) : allRows;
    this.skeletonRows = this.loading ? SKELETON_ROWS : [];
    this.emptyMessage = this.resolveEmptyMessage(isSearching);
    this.hasNoResults = isSearching && !this.loading && this.rows.length === 0;

    const shown = new Set(
      allRows.flatMap((row) => row.panels.map((panel) => panel.packageId))
    );
    this.pendingDismissPackageIds = this.expandedPackageIds.filter(
      (packageId) => !shown.has(packageId)
    );

    this.canPageBack = this.windowStart > 0;
    this.canPageForward =
      this.windowStart < Math.max(0, this.axis.length - this.visibleSlots);
  }

  private resolveEmptyMessage(isSearching: boolean): string | null {
    if (isSearching || this.loading || this.rows.length > 0) {
      return null;
    }
    return this.loadFailed
      ? "Couldn't load the notary schedule."
      : "No notaries scheduled for this day.";
  }

  /** Slots prepended by a widened axis shift every index; keep the window on the same clock time. */
  private adoptAxis(axis: SlotAxis): void {
    if (this.windowInitialized) {
      this.windowStart +=
        (this.axis[0].startMinutes - axis[0].startMinutes) / SLOT_MINUTES;
    }
    this.axis = axis;
  }

  private buildPanels(
    lane: GridLane,
    expanded: Set<number>,
    inView: Set<number>
  ): GridPanel[] {
    return this.closings
      .filter(
        (closing) =>
          expanded.has(closing.packageId) &&
          inView.has(closing.packageId) &&
          laneIdForClosing(closing) === lane.laneId
      )
      .sort(
        (left, right) =>
          left.scheduledDateTime.localeCompare(right.scheduledDateTime) ||
          left.packageId - right.packageId
      )
      .map((closing) => ({
        packageId: closing.packageId,
        panelId: `rcm-closing-detail-${closing.packageId}`,
        variant: closing.variant,
        ariaLabel: `Closing details for ${lane.name} at ${formatClockTime(
          new Date(closing.scheduledDateTime)
        )}`,
      }));
  }

  private flushPendingDismiss(): void {
    if (this.pendingDismissPackageIds.length === 0) {
      return;
    }
    const packageIds = this.pendingDismissPackageIds;
    this.pendingDismissPackageIds = [];
    packageIds.forEach((packageId) => this.panelDismiss.emit(packageId));
  }

  private buildCell(
    lane: GridLane,
    column: SlotColumn,
    buckets: Map<string, ClosingPill>,
    current: number | null,
    expanded: Set<number>
  ): GridCell {
    const pill = buckets.get(bucketKey(lane.laneId, column.index)) ?? null;
    const open = pill === null && isOpenSlot(buckets, lane, column);

    return {
      key: bucketKey(lane.laneId, column.index),
      isCurrent: column.index === current,
      packageId: pill?.packageId ?? null,
      pill,
      presentation: pill
        ? slotPresentation(pill.variant, pill.delayMinutes, pill.icon)
        : open
          ? openSlotPresentation()
          : emptyPresentation(),
      interactive: pill !== null,
      expanded: pill ? expanded.has(pill.packageId) : false,
      controls: pill ? `rcm-closing-detail-${pill.packageId}` : null,
    };
  }
}
